import type {
    ApiCommerceBoardHeader,
    ApiCommerceBoardHeaderLayer,
    ApiCommerceImageAsset,
} from "@bridge-applications/pb-types";
import type { CSSProperties, ReactNode } from "react";
import { useState } from "react";
import {
    localizedValueCandidates,
    resolveLocalizedText,
} from "../../../../../../../logic/helpers/localizedText";
import "./ChainOfferCampaignHeader.scss";

interface ChainOfferCampaignHeaderProps {
    header: ApiCommerceBoardHeader;
    locale: string;
    closeControl: ReactNode;
    countdownContent: ReactNode;
}

interface RenderedHeaderLayer {
    layer: ApiCommerceBoardHeaderLayer;
    image: ApiCommerceImageAsset;
    layerIndex: number;
}

interface LayerStyle extends CSSProperties {
    "--chain-offer-header-layer-center-x": string;
    "--chain-offer-header-layer-center-y": string;
    "--chain-offer-header-layer-width": string;
    "--chain-offer-header-layer-rotation": string;
    "--chain-offer-header-layer-opacity": number;
}

function resolveLayerAsset(
    layer: ApiCommerceBoardHeaderLayer,
    locale: string,
    failedImageUrls: ReadonlySet<string>,
): ApiCommerceImageAsset | undefined {
    if (layer.source.type === "asset") {
        return failedImageUrls.has(layer.source.asset.url)
            ? undefined
            : layer.source.asset;
    }
    return localizedValueCandidates(layer.source.assets, locale).find(
        (asset) => !failedImageUrls.has(asset.url),
    );
}

function layerRenderedHeightPercentage(
    header: ApiCommerceBoardHeader,
    { layer, image }: RenderedHeaderLayer,
): number {
    if (
        image.width === undefined ||
        image.height === undefined ||
        image.width <= 0 ||
        image.height <= 0
    ) {
        return 0;
    }

    return (
        layer.layout.width *
        (image.height / image.width) *
        header.canvas.aspectRatio
    );
}

/**
 * Centers the close control on the first layer's top-right corner, but keeps
 * it inside the header box so it is never clipped by the dialog or pushed
 * outside the viewport on narrow screens.
 */
function firstLayerTopRightStyle(
    header: ApiCommerceBoardHeader,
    renderedLayer: RenderedHeaderLayer | undefined,
): CSSProperties {
    const inset = "var(--chain-offer-header-close-inset)";
    if (renderedLayer === undefined) {
        return { top: inset, left: `calc(100% - ${inset})` };
    }

    const { layer } = renderedLayer;
    const layerHeight = layerRenderedHeightPercentage(header, renderedLayer);
    const cornerTop = `${layer.layout.centerY - layerHeight / 2}%`;
    const cornerLeft = `${layer.layout.centerX + layer.layout.width / 2}%`;
    return {
        top: `max(${cornerTop}, ${inset})`,
        left: `min(${cornerLeft}, calc(100% - ${inset}))`,
    };
}

function firstLayerBottomCenterStyle(
    header: ApiCommerceBoardHeader,
    renderedLayer: RenderedHeaderLayer | undefined,
): CSSProperties {
    if (renderedLayer === undefined) {
        return { top: "100%", left: "50%" };
    }

    const { layer } = renderedLayer;
    const layerHeight = layerRenderedHeightPercentage(header, renderedLayer);
    if (layerHeight === 0) {
        return { top: "100%", left: `${layer.layout.centerX}%` };
    }

    return {
        top: `calc(${layer.layout.centerY + layerHeight / 2}% - 20px)`,
        left: `${layer.layout.centerX}%`,
    };
}

export function ChainOfferCampaignHeader({
    header,
    locale,
    closeControl,
    countdownContent,
}: ChainOfferCampaignHeaderProps) {
    const [failedImageUrls, setFailedImageUrls] = useState<ReadonlySet<string>>(
        () => new Set(),
    );
    const accessibilityLabel = resolveLocalizedText(
        header.accessibilityLabel,
        locale,
    );
    const renderedLayers: RenderedHeaderLayer[] = [];

    for (const [layerIndex, layer] of header.layers.entries()) {
        const image = resolveLayerAsset(layer, locale, failedImageUrls);
        if (layer.layout.visible !== false && image !== undefined) {
            renderedLayers.push({ layer, image, layerIndex });
        }
    }

    const firstRenderedLayer = renderedLayers[0];

    return (
        <div
            className="chain-offer-campaign-header"
            style={{ aspectRatio: header.canvas.aspectRatio }}
        >
            <div
                className="chain-offer-campaign-header__artwork"
                role="img"
                aria-label={accessibilityLabel}
            >
                {renderedLayers.map(({ layer, image, layerIndex }) => {
                    const layerStyle: LayerStyle = {
                        "--chain-offer-header-layer-center-x": `${layer.layout.centerX}%`,
                        "--chain-offer-header-layer-center-y": `${layer.layout.centerY}%`,
                        "--chain-offer-header-layer-width": `${layer.layout.width}%`,
                        "--chain-offer-header-layer-rotation": `${layer.layout.rotationDegrees ?? 0}deg`,
                        "--chain-offer-header-layer-opacity":
                            layer.layout.opacity ?? 1,
                    };

                    return (
                        <img
                            key={layer.id}
                            className="chain-offer-campaign-header__layer"
                            data-layer-id={layer.id}
                            data-layer-stack={
                                layerIndex === 0 ? "background" : "overlay"
                            }
                            style={layerStyle}
                            src={image.url}
                            width={image.width}
                            height={image.height}
                            alt=""
                            aria-hidden="true"
                            decoding="async"
                            loading="eager"
                            draggable={false}
                            referrerPolicy="no-referrer"
                            onError={() =>
                                setFailedImageUrls((current) => {
                                    const next = new Set(current);
                                    next.add(image.url);
                                    return next;
                                })
                            }
                        />
                    );
                })}
            </div>
            <div
                className="chain-offer-campaign-header__close-anchor"
                style={firstLayerTopRightStyle(header, firstRenderedLayer)}
            >
                {closeControl}
            </div>
            <div
                className="chain-offer-campaign-header__countdown-anchor"
                style={firstLayerBottomCenterStyle(header, firstRenderedLayer)}
            >
                {countdownContent}
            </div>
        </div>
    );
}
